import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useLoginForm } from '@/hooks/useLoginForm';
import EmailInput from '@/components/auth/form/EmailInput';
import PasswordInput from '@/components/auth/form/PasswordInput';
import GeneralError from '@/components/auth/form/GeneralError';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface LoginFormProps {
  loading: boolean;
  setLoading: (loading: boolean) => void;
  onForgotPassword?: () => void;
  returnTo?: string | null;
}

const LoginForm = ({ loading, setLoading, onForgotPassword, returnTo }: LoginFormProps) => {
  const {
    email,
    setEmail,
    password,
    setPassword,
    showPassword,
    error,
    verificationSent,
    togglePasswordVisibility,
    resetError,
    handleLogin,
    resendVerificationEmail,
  } = useLoginForm(setLoading, returnTo);

  return (
    <Card className="w-full max-w-sm mx-auto border-0 shadow-sm">
      <CardHeader className="text-center pt-10 pb-6">
        <CardTitle className="text-[17px] font-normal font-prociono tracking-wide">
          Log in to your WCD account
        </CardTitle>
        <CardDescription className="pt-1 text-sm text-gray-600">
          For existing clients and designers who already have an account.
        </CardDescription>
      </CardHeader>
      <CardContent className="pb-10">
        <form onSubmit={handleLogin} className="space-y-4">
          <GeneralError error={error} />

          {verificationSent && (
            <div className="p-4 border border-yellow-200 bg-yellow-50 rounded-md space-y-3">
              <div className="flex items-start text-amber-700 text-sm">
                <AlertTriangle size={18} className="mr-2 mt-0.5 flex-shrink-0" />
                <span>Email verification required. Please check your inbox and click the verification link.</span>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={resendVerificationEmail}
                disabled={loading}
                className="flex items-center text-sm"
              >
                <RefreshCw size={14} className="mr-1" />
                Resend verification email
              </Button>
            </div>
          )}

          <EmailInput
            email={email}
            setEmail={setEmail}
            emailError=""
            resetEmailError={resetError}
          />

          <PasswordInput
            password={password}
            setPassword={setPassword}
            showPassword={showPassword}
            togglePasswordVisibility={togglePasswordVisibility}
            passwordError=""
            resetPasswordError={resetError}
          />

          {onForgotPassword && (
            <div className="flex justify-end">
              <Button
                type="button"
                variant="link"
                size="sm"
                className="text-sm text-muted-foreground hover:text-primary p-0 h-auto"
                onClick={onForgotPassword}
              >
                Forgot password?
              </Button>
            </div>
          )}

          <Button
            type="submit"
            className="w-full bg-[#2c1810] hover:bg-[#1a0f09] text-white font-normal"
            disabled={loading}
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Logging in...
              </div>
            ) : (
              'Login'
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default LoginForm;
